import type { DropdownData } from '../../interfaces/input.interface';
import type {
    ReactiveFormSupportDataCacheStorage,
    ReactiveFormSupportDataContext,
    ReactiveFormSupportDataDiagnosticsThreshold,
    ReactiveFormSupportDataEndpoint,
    ReactiveFormSupportDataErrorHandler,
    ReactiveFormSupportDataLabelSelector,
    ReactiveFormSupportDataParams,
    ReactiveFormSupportDataPhase,
    ReactiveFormSupportDataResponseMapper,
    ReactiveFormSupportDataSource,
    ReactiveFormSupportDataSuccessHandler,
    ReactiveFormSupportDataTarget,
} from '../../interfaces/form.interface';

export type ReactiveFormSupportDataThresholdResolver<TKey extends string = string> = (
    key: TKey,
) => ReactiveFormSupportDataDiagnosticsThreshold;

export interface ReactiveFormSupportDataSourceFactoryDefaults<TKey extends string = string> {
    readonly ttlMs?: number;
    readonly staleTtlMs?: number;
    readonly cacheStorage?: ReactiveFormSupportDataCacheStorage;
    readonly staleWhileRevalidate?: boolean;
    readonly diagnosticsThreshold?: ReactiveFormSupportDataDiagnosticsThreshold | ReactiveFormSupportDataThresholdResolver<TKey>;
}

export interface ReactiveFormSupportDataSourceDefinition<TModel = unknown, TKey extends string = string> {
    readonly key: TKey;
    readonly phase?: ReactiveFormSupportDataPhase | undefined;
    readonly cacheKey?: string | undefined;
    readonly ttlMs?: number | undefined;
    readonly staleTtlMs?: number | undefined;
    readonly cacheStorage?: ReactiveFormSupportDataCacheStorage | undefined;
    readonly staleWhileRevalidate?: boolean | undefined;
    readonly dependsOn?: readonly string[] | undefined;
    readonly endpoint?: unknown;
    readonly params?: ReactiveFormSupportDataParams<TModel> | undefined;
    readonly target?: ReactiveFormSupportDataTarget<TModel> | undefined;
    readonly label?: ReactiveFormSupportDataLabelSelector | undefined;
    readonly labelKey?: string | undefined;
    readonly labelKeys?: readonly string[] | undefined;
    readonly noPlaceholder?: boolean | undefined;
    readonly preserveExistingValues?: boolean | undefined;
    readonly timeoutMs?: number | undefined;
    readonly diagnosticsThreshold?: ReactiveFormSupportDataDiagnosticsThreshold | undefined;
    readonly mapResponse?: ReactiveFormSupportDataResponseMapper<TModel> | undefined;
    readonly onSuccess?: ReactiveFormSupportDataSuccessHandler<TModel> | undefined;
    readonly onError?: ReactiveFormSupportDataErrorHandler<TModel> | undefined;
    readonly load?: ((context: ReactiveFormSupportDataContext<TModel>) => Promise<unknown> | unknown) | undefined;
}

export interface ReactiveFormSupportDataDropdownSourceDefinition<TModel = unknown, TKey extends string = string>
    extends Omit<ReactiveFormSupportDataSourceDefinition<TModel, TKey>, 'endpoint' | 'target'> {
    readonly endpoint: unknown;
    readonly target: DropdownData<unknown>;
}

/**
 * Creates strict, reusable support-data source declarations without leaking form-specific wiring into hosts.
 */
export class ReactiveFormSupportDataSourceFactory<TModel = unknown, TKey extends string = string> {
    public constructor(private readonly defaults: ReactiveFormSupportDataSourceFactoryDefaults<TKey> = {}) {}

    public dropdownSource(definition: ReactiveFormSupportDataDropdownSourceDefinition<TModel, TKey>): ReactiveFormSupportDataSource<TModel> {
        return this.source({
            ...definition,
            endpoint: ReactiveFormSupportDataSourceFactory.toEndpoint(definition.endpoint),
            target: definition.target,
        });
    }

    public source(definition: ReactiveFormSupportDataSourceDefinition<TModel, TKey>): ReactiveFormSupportDataSource<TModel> {
        const source: Partial<ReactiveFormSupportDataSource<TModel>> & Pick<ReactiveFormSupportDataSource<TModel>, 'key'> = {
            key: definition.key,
        };

        this.assign(source, 'phase', definition.phase);
        this.assign(source, 'cacheKey', definition.cacheKey);
        this.assign(source, 'ttlMs', definition.ttlMs ?? this.defaults.ttlMs);
        this.assign(source, 'staleTtlMs', definition.staleTtlMs ?? this.defaults.staleTtlMs);
        this.assign(source, 'cacheStorage', definition.cacheStorage ?? this.defaults.cacheStorage);
        this.assign(source, 'staleWhileRevalidate', definition.staleWhileRevalidate ?? this.defaults.staleWhileRevalidate);
        this.assign(source, 'dependsOn', definition.dependsOn);
        this.assign(source, 'endpoint', this.resolveEndpoint(definition.endpoint));
        this.assign(source, 'params', definition.params);
        this.assign(source, 'target', definition.target);
        this.assign(source, 'label', definition.label);
        this.assign(source, 'labelKey', definition.labelKey);
        this.assign(source, 'labelKeys', definition.labelKeys);
        this.assign(source, 'noPlaceholder', definition.noPlaceholder);
        this.assign(source, 'preserveExistingValues', definition.preserveExistingValues);
        this.assign(source, 'timeoutMs', definition.timeoutMs);
        this.assign(source, 'diagnosticsThreshold', this.resolveDiagnosticsThreshold(definition));
        this.assign(source, 'mapResponse', definition.mapResponse);
        this.assign(source, 'onSuccess', definition.onSuccess);
        this.assign(source, 'onError', definition.onError);
        this.assign(source, 'load', definition.load);

        return source as ReactiveFormSupportDataSource<TModel>;
    }

    public static toEndpoint(endpoint: unknown): ReactiveFormSupportDataEndpoint {
        return endpoint as ReactiveFormSupportDataEndpoint;
    }

    private resolveEndpoint(endpoint: unknown): ReactiveFormSupportDataEndpoint | undefined {
        return endpoint === undefined ? undefined : ReactiveFormSupportDataSourceFactory.toEndpoint(endpoint);
    }

    private resolveDiagnosticsThreshold(
        definition: ReactiveFormSupportDataSourceDefinition<TModel, TKey>,
    ): ReactiveFormSupportDataDiagnosticsThreshold | undefined {
        if (definition.diagnosticsThreshold !== undefined) {
            return definition.diagnosticsThreshold;
        }

        if (typeof this.defaults.diagnosticsThreshold === 'function') {
            return this.defaults.diagnosticsThreshold(definition.key);
        }

        return this.defaults.diagnosticsThreshold;
    }

    private assign<TKeyName extends keyof ReactiveFormSupportDataSource<TModel>>(
        source: Partial<ReactiveFormSupportDataSource<TModel>>,
        key: TKeyName,
        value: ReactiveFormSupportDataSource<TModel>[TKeyName] | undefined,
    ): void {
        if (value === undefined) {
            return;
        }

        source[key] = value;
    }
}
